import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  agregarMiembroEquipoApi,
  guardarConsultoriosEquipoApi,
  obtenerCatalogoProfesionalesApi,
  obtenerEquipoApi,
  precargarEquipoApi,
  quitarMiembroEquipoApi,
} from '../services/api';
import { DIAS, claseDisciplina } from '../utils/agenda';

// Equipo Cenein, armado como un árbol: centro → área → profesionales, y el
// consultorio de cada profesional según el día de la semana.
const inputClass =
  'rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100';
const botonSecundario =
  'inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200';
const botonPrimario = 'rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50';
const tarjeta = 'rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800';
const opcion = (activa) => `flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2 text-left text-sm transition ${
  activa
    ? 'bg-emerald-50 font-semibold text-emerald-800 ring-1 ring-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-300 dark:ring-emerald-700'
    : 'text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700'
}`;

function Columna({ titulo, children }) {
  return (
    <section className={`${tarjeta} p-3`} aria-label={titulo}>
      <h2 className="mb-2 px-1 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{titulo}</h2>
      {children}
    </section>
  );
}

function ConsultoriosPorDia({ miembro, consultorios, editable, alGuardar }) {
  const [valores, setValores] = useState(miembro.consultorios);
  const [guardando, setGuardando] = useState(false);
  useEffect(() => setValores(miembro.consultorios), [miembro.consultorios]);
  const cambiado = DIAS.some(({ key }) => (valores[key] || '') !== (miembro.consultorios[key] || ''));
  const guardar = async () => {
    setGuardando(true);
    try { await alGuardar(valores); } finally { setGuardando(false); }
  };
  return (
    <div className="mt-2 space-y-2">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {DIAS.map(({ key, label }) => {
          const actual = valores[key] || '';
          // Un nombre que la agenda ya no tiene se sigue mostrando para no perderlo.
          const opciones = actual && !consultorios.includes(actual) ? [...consultorios, actual] : consultorios;
          return (
            <label key={key} className="flex flex-col gap-1 text-xs font-semibold text-slate-500 dark:text-slate-400">
              {label}
              {editable ? (
                <select
                  aria-label={`Consultorio del ${label.toLowerCase()} de ${miembro.profesional}`}
                  className={inputClass}
                  value={actual}
                  onChange={(e) => setValores((v) => ({ ...v, [key]: e.target.value }))}
                >
                  <option value="">Sin consultorio</option>
                  {opciones.map((c) => <option key={c} value={c}>{consultorios.includes(c) ? c : `${c} (ya no está en la agenda)`}</option>)}
                </select>
              ) : (
                <span className="text-sm font-normal text-slate-800 dark:text-slate-100">{actual || '—'}</span>
              )}
            </label>
          );
        })}
      </div>
      {editable && cambiado && (
        <div className="flex justify-end gap-2">
          <button type="button" className={botonSecundario} onClick={() => setValores(miembro.consultorios)} disabled={guardando}>Descartar</button>
          <button type="button" className={botonPrimario} onClick={guardar} disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar consultorios'}</button>
        </div>
      )}
    </div>
  );
}

export default function Equipo({ usuario }) {
  const editable = Boolean(usuario?.isAdmin);
  const [datos, setDatos] = useState(null);
  const [catalogo, setCatalogo] = useState([]);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [sedeId, setSedeId] = useState(null);
  const [area, setArea] = useState(null);
  const [abierto, setAbierto] = useState(null);
  const [nuevo, setNuevo] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const [equipo, profesionales] = await Promise.all([obtenerEquipoApi(), obtenerCatalogoProfesionalesApi()]);
      setDatos(equipo);
      setCatalogo(profesionales);
      setSedeId((actual) => (equipo.sedes.some((s) => s.id === actual) ? actual : equipo.sedes[0]?.id ?? null));
      setArea((actual) => (equipo.areas.includes(actual) ? actual : equipo.areas[0] ?? null));
    } catch (err) {
      setError(err.message || 'No se pudo cargar el equipo.');
    }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const sede = datos?.sedes.find((s) => s.id === sedeId) || null;
  const delCentro = useMemo(() => (datos?.miembros || []).filter((m) => m.sedeId === sedeId), [datos, sedeId]);
  const delArea = delCentro.filter((m) => m.area === area);
  const disponibles = catalogo.filter((p) => !delArea.some((m) => m.profesionalId === p.id));

  const accion = async (fn, exito) => {
    setError(''); setAviso(''); setOcupado(true);
    try {
      const r = await fn();
      if (exito) setAviso(exito(r));
      await cargar();
      return r;
    } catch (err) {
      setError(err.message || 'No se pudo completar la operación.');
      return null;
    } finally {
      setOcupado(false);
    }
  };

  const agregar = async (e) => {
    e.preventDefault();
    if (!nuevo) return;
    const r = await accion(() => agregarMiembroEquipoApi({ sedeId, area, profesionalId: Number(nuevo) }));
    if (r) { setNuevo(''); setAbierto(r.id); }
  };
  const quitar = (m) => {
    if (!window.confirm(`¿Sacar a ${m.profesional} de ${m.area} en ${sede?.nombre}? No se borra del catálogo de profesionales.`)) return;
    accion(() => quitarMiembroEquipoApi(m.id));
  };
  const precargar = () => accion(() => precargarEquipoApi(sedeId), (r) => {
    const partes = [`Se agregaron ${r.miembros} profesional${r.miembros === 1 ? '' : 'es'} y ${r.dias} día${r.dias === 1 ? '' : 's'} con consultorio.`];
    if (r.sinArea.length) partes.push(`Sin área clara en la agenda (agregalos a mano): ${r.sinArea.join(', ')}.`);
    return partes.join(' ');
  });

  if (!datos) {
    return <div className="p-8 text-sm text-slate-500">{error || 'Cargando equipo…'}</div>;
  }

  return (
    <div className="mx-auto max-w-7xl space-y-4 p-4 sm:p-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-black text-slate-800 dark:text-slate-100">Equipo Cenein</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">Elegí un centro y un área para ver sus profesionales y el consultorio de cada día.</p>
        </div>
        {editable && sede && (
          <button type="button" className={botonSecundario} onClick={precargar} disabled={ocupado}
            title="Agrega los profesionales y consultorios que figuran en la agenda general. No cambia lo que ya está cargado.">
            <span className="material-symbols-outlined text-[18px]">auto_fix_high</span>
            Completar {sede.nombre} desde la agenda
          </button>
        )}
      </header>

      {error && <p role="alert" className="rounded-xl bg-red-50 px-4 py-2 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300">{error}</p>}
      {aviso && <p role="status" className="rounded-xl bg-emerald-50 px-4 py-2 text-sm text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">{aviso}</p>}

      {!datos.sedes.length ? (
        <p className={`${tarjeta} p-6 text-sm text-slate-500`}>Todavía no hay centros: se crean al importar la agenda general en Agendas.</p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,2.5fr)]">
          <Columna titulo="Centros">
            <ul className="space-y-1">
              {datos.sedes.map((s) => (
                <li key={s.id}>
                  <button type="button" className={opcion(s.id === sedeId)} aria-pressed={s.id === sedeId} onClick={() => { setSedeId(s.id); setAbierto(null); }}>
                    <span>{s.nombre}</span>
                    <span className="text-xs text-slate-400">{datos.miembros.filter((m) => m.sedeId === s.id).length}</span>
                  </button>
                </li>
              ))}
            </ul>
          </Columna>

          <Columna titulo="Áreas">
            <ul className="space-y-1">
              {datos.areas.map((a) => (
                <li key={a}>
                  <button type="button" className={opcion(a === area)} aria-pressed={a === area} onClick={() => { setArea(a); setAbierto(null); }}>
                    <span className="flex items-center gap-2">
                      <span className={`inline-block h-3 w-3 rounded-full ${claseDisciplina(a)}`} aria-hidden="true" />
                      {a}
                    </span>
                    <span className="text-xs text-slate-400">{delCentro.filter((m) => m.area === a).length}</span>
                  </button>
                </li>
              ))}
            </ul>
          </Columna>

          <Columna titulo={`Profesionales · ${area || ''}${sede ? ` · ${sede.nombre}` : ''}`}>
            {editable && (
              <form onSubmit={agregar} className="mb-3 flex flex-wrap gap-2">
                <select aria-label="Profesional para agregar" className={`${inputClass} min-w-0 flex-1`} value={nuevo} onChange={(e) => setNuevo(e.target.value)}>
                  <option value="">Elegí un profesional…</option>
                  {disponibles.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                </select>
                <button type="submit" className={botonPrimario} disabled={!nuevo || ocupado}>Agregar</button>
              </form>
            )}
            {!delArea.length ? (
              <p className="px-1 py-4 text-sm text-slate-500 dark:text-slate-400">No hay profesionales en esta área.</p>
            ) : (
              <ul className="space-y-2">
                {delArea.map((m) => {
                  const dias = DIAS.filter(({ key }) => m.consultorios[key]).length;
                  return (
                    <li key={m.id} className="rounded-xl border border-slate-200 p-3 dark:border-slate-700">
                      <div className="flex items-center justify-between gap-2">
                        <button type="button" className="flex min-w-0 flex-1 items-center gap-2 text-left" aria-expanded={abierto === m.id}
                          aria-label={`Consultorios de ${m.profesional}`} onClick={() => setAbierto(abierto === m.id ? null : m.id)}>
                          <span className="material-symbols-outlined text-[18px] text-slate-400">{abierto === m.id ? 'expand_less' : 'expand_more'}</span>
                          <span className="truncate font-semibold text-slate-800 dark:text-slate-100">{m.profesional}</span>
                          <span className="shrink-0 text-xs text-slate-400">{dias ? `${dias} día${dias === 1 ? '' : 's'} con consultorio` : 'sin consultorio'}</span>
                        </button>
                        {editable && (
                          <button type="button" className="rounded-lg p-1 text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30"
                            aria-label={`Sacar a ${m.profesional}`} onClick={() => quitar(m)} disabled={ocupado}>
                            <span className="material-symbols-outlined text-[18px]">delete</span>
                          </button>
                        )}
                      </div>
                      {abierto === m.id && (
                        <ConsultoriosPorDia miembro={m} consultorios={sede?.consultorios || []} editable={editable}
                          alGuardar={(valores) => accion(() => guardarConsultoriosEquipoApi(m.id, valores), () => `Consultorios de ${m.profesional} guardados.`)} />
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </Columna>
        </div>
      )}
    </div>
  );
}
